/**
 * Smart filtering categories for program types
 * These align with Google Places API types and real-world venue categories
 */
export const PROGRAM_TYPE_CATEGORIES = {
  camps: {
    value: 'camps',
    label: 'Camps',
    icon: '🏕️',
    description: 'Summer camps, day camps, overnight camps',
    color: '#10B981', // green-500
    googlePlacesTypes: ['campground', 'rv_park'],
    keywords: [
      'camp', 'summer camp', 'day camp', 'overnight camp', 
      'sleepaway camp', 'resident camp', 'camping'
    ]
  },
  
  sportsRecreation: {
    value: 'sports_recreation',
    label: 'Sports & Recreation',
    icon: '⚽',
    description: 'Sports clubs, gyms, recreation centers',
    color: '#3B82F6', // blue-500
    googlePlacesTypes: ['gym', 'sports_complex', 'stadium'],
    keywords: [
      'sports', 'athletic', 'fitness', 'recreation', 'league', 
      'club', 'soccer', 'basketball', 'baseball', 'tennis'
    ]
  },
  
  schoolsEducation: {
    value: 'schools_education',
    label: 'Schools & Education',
    icon: '🏫',
    description: 'Schools, universities offering programs',
    color: '#8B5CF6', // purple-500
    googlePlacesTypes: ['school', 'university', 'secondary_school', 'primary_school'],
    keywords: [
      'school', 'academy', 'learning center', 'education', 
      'enrichment', 'tutoring', 'stem', 'academic'
    ]
  },
  
  artsCulture: {
    value: 'arts_culture',
    label: 'Arts & Culture',
    icon: '🎨',
    description: 'Museums, art centers, theaters',
    color: '#EC4899', // pink-500
    googlePlacesTypes: ['museum', 'art_gallery', 'performing_arts_theater', 'library'],
    keywords: [
      'art', 'museum', 'theater', 'music', 'dance', 'drama', 
      'cultural', 'performing arts', 'visual arts', 'creative'
    ]
  },
  
  communityCenters: {
    value: 'community_centers',
    label: 'Community Centers',
    icon: '🏢',
    description: 'YMCA, Boys & Girls Clubs, community organizations',
    color: '#F59E0B', // amber-500
    googlePlacesTypes: ['community_center'],
    keywords: [
      'ymca', 'ywca', 'boys girls club', 'community center', 
      'youth center', 'jcc', 'jewish community', 'rec center'
    ]
  },
  
  parksOutdoor: {
    value: 'parks_outdoor',
    label: 'Parks & Outdoor',
    icon: '🌲',
    description: 'Parks, outdoor education centers, nature centers',
    color: '#14B8A6', // teal-500
    googlePlacesTypes: ['park', 'national_park', 'tourist_attraction'],
    keywords: [
      'park', 'nature center', 'outdoor', 'environmental', 
      'wildlife', 'hiking', 'nature', 'conservation', 'botanical'
    ]
  }
};

/**
 * All filter type definitions
 */
export const FILTER_TYPES = {
  // Program Type (multi-select)
  programType: {
    label: 'Program Type',
    icon: '🎯',
    options: Object.values(PROGRAM_TYPE_CATEGORIES).map(cat => ({
      value: cat.value,
      label: cat.label,
      icon: cat.icon,
      description: cat.description
    }))
  },
  
  // Age Range (multi-select)
  ageRange: {
    label: 'Age Range',
    icon: '👶',
    options: [
      { value: '3-5', label: '3-5 years' },
      { value: '6-8', label: '6-8 years' },
      { value: '9-12', label: '9-12 years' },
      { value: '13-15', label: '13-15 years' },
      { value: '16-18', label: '16-18 years' }
    ]
  },
  
  // Price Range (slider)
  priceRange: {
    label: 'Price Range (per week)',
    icon: '💰',
    min: 0,
    max: 1000,
    step: 25,
    defaultMax: 1000
  },
  
  // Schedule/Duration (multi-select)
  schedule: {
    label: 'Schedule',
    icon: '📅',
    options: [
      { value: 'full-day', label: 'Full Day' },
      { value: 'half-day', label: 'Half Day' },
      { value: 'extended-care', label: 'Extended Care' },
      { value: 'overnight', label: 'Overnight' }
    ]
  },
  
  // Features & Amenities (multi-select)
  features: {
    label: 'Features & Amenities',
    icon: '✨',
    options: [
      { value: 'transportation', label: 'Transportation' },
      { value: 'lunch', label: 'Lunch Provided' },
      { value: 'field-trips', label: 'Field Trips' },
      { value: 'swimming', label: 'Swimming' },
      { value: 'special-needs', label: 'Special Needs Support' }
    ]
  },
  
  // Dates/Availability (date range)
  availability: {
    label: 'Dates Available',
    icon: '📆',
    type: 'dateRange'
  },
  
  // Rating (minimum rating filter)
  rating: {
    label: 'Minimum Rating',
    icon: '⭐',
    options: [
      { value: 4.5, label: '4.5+ stars' },
      { value: 4.0, label: '4.0+ stars' },
      { value: 3.5, label: '3.5+ stars' }
    ]
  }
};

/**
 * Default filter state
 */
export const DEFAULT_FILTERS = {
  programType: [],
  ageRange: [],
  priceRange: { min: 0, max: 1000 },
  schedule: [],
  features: [],
  rating: null,
  availability: { startDate: null, endDate: null }
};
